import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { trackClick } from "@/lib/api";
import { CheckCircle2, MessageCircle, User, Store, Sparkles } from "lucide-react";

const INITIAL = {
  first_name: "", last_name: "", age: "", city: "", province: "",
  phone: "", email: "", instagram: "", tiktok: "",
  application_type: "persona_fisica", business_name: "",
  has_vat: "no", has_experience: "no", reach: "",
  presentation: "", referral: "", privacy_consent: false,
};

const APP_TYPES = [
  { value: "persona_fisica", label: "Persona fisica" },
  { value: "attivita_commerciale", label: "Attività commerciale" },
  { value: "creator", label: "Creator o pagina social" },
  { value: "collaboratore", label: "Collaboratore commerciale" },
];

export default function LavoraConNoi() {
  const s = useSettings();
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_consent) {
      toast.error("Devi accettare l'informativa privacy.");
      return;
    }
    setSending(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      setForm(INITIAL);
      toast.success("Candidatura inviata correttamente.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const input =
    "w-full rounded-lg bg-black/40 border border-white/10 focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/30 focus:outline-none px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 transition-colors duration-200";

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-14 sm:py-20">
      <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Collaborazioni</span>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter">
        Trasforma la tua passione in una <span className="text-yellow-400">collaborazione</span>
      </h1>
      <p className="mt-4 max-w-3xl text-zinc-400 leading-relaxed">
        Vinko seleziona persone e attività interessate a collaborare nel settore
        delle scommesse, delle promozioni digitali e dell'acquisizione di nuovi
        utenti. Cerchiamo persone dinamiche, appassionate di sport,
        comunicazione e mondo digitale. Non è richiesta necessariamente
        esperienza nel settore: verranno fornite informazioni, materiali
        commerciali e supporto iniziale.
      </p>

      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border border-white/10 bg-[#101010] p-6">
          <div className="h-11 w-11 rounded-xl bg-yellow-400/10 border border-yellow-400/25 grid place-items-center text-yellow-400 mb-4">
            <User className="w-5 h-5" />
          </div>
          <h3 className="text-xl font-bold">Collabora come persona</h3>
          <ul className="mt-3 space-y-1.5 text-sm text-zinc-300">
            {["Appassionati di sport", "Creator", "Gestori di pagine social", "Persone con una rete di contatti", "Collaboratori commerciali", "Persone interessate a creare una nuova entrata"].map((t) => (
              <li key={t} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-yellow-400 shrink-0" />{t}</li>
            ))}
          </ul>
          <a href="#form" className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-5 py-3 transition-transform duration-200 hover:scale-[1.02]">
            CANDIDATI COME COLLABORATORE
          </a>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[#101010] p-6">
          <div className="h-11 w-11 rounded-xl bg-yellow-400/10 border border-yellow-400/25 grid place-items-center text-yellow-400 mb-4">
            <Store className="w-5 h-5" />
          </div>
          <h3 className="text-xl font-bold">Collabora con la tua attività</h3>
          <ul className="mt-3 space-y-1.5 text-sm text-zinc-300">
            {["Bar", "Tabaccherie", "Sale giochi", "Internet point", "Agenzie", "Negozi", "Associazioni sportive", "Professionisti"].map((t) => (
              <li key={t} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-yellow-400 shrink-0" />{t}</li>
            ))}
          </ul>
          <a href="#form" className="mt-5 inline-flex items-center justify-center rounded-full bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-5 py-3 transition-transform duration-200 hover:scale-[1.02]">
            CANDIDA LA TUA ATTIVITÀ
          </a>
        </div>
      </div>

      <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
        Le modalità di collaborazione, i compensi e gli eventuali requisiti
        fiscali o contrattuali vengono valutati singolarmente prima dell'avvio
        della collaborazione.
      </p>

      <div className="mt-6">
        <a
          href={s.whatsapp_url}
          target="_blank" rel="noopener"
          onClick={() => trackClick("whatsapp")}
          data-testid="lcn-whatsapp"
          className="inline-flex items-center gap-2 rounded-full border border-yellow-400/60 hover:bg-yellow-400/10 text-yellow-400 font-bold text-sm px-5 py-3 transition-colors duration-200"
        >
          <MessageCircle className="w-4 h-4" /> RICHIEDI INFORMAZIONI SULLA COLLABORAZIONE
        </a>
      </div>

      {/* FORM */}
      <div id="form" className="mt-14 rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 text-yellow-400" />
          <span className="text-xs font-bold uppercase tracking-[0.25em] text-yellow-500">Candidatura</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Invia la tua candidatura</h2>

        {done ? (
          <div className="mt-6 rounded-xl border border-yellow-400/40 bg-yellow-400/5 p-6 flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-yellow-400 shrink-0" />
            <div>
              <div className="font-semibold text-white">Candidatura inviata correttamente.</div>
              <p className="text-sm text-zinc-300 mt-1">
                Il team Vinko valuterà la richiesta e ti contatterà utilizzando i recapiti indicati.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-4 text-sm text-yellow-400 hover:text-yellow-300 font-semibold"
              >
                Invia un'altra candidatura →
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-4 md:grid-cols-2" data-testid="apply-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Nome *</label>
              <input required className={input} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="apply-first-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Cognome *</label>
              <input required className={input} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="apply-last-name" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Età</label>
              <input className={input} value={form.age} onChange={(e) => set("age", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Città</label>
              <input className={input} value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Provincia</label>
              <input className={input} value={form.province} onChange={(e) => set("province", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Telefono *</label>
              <input required className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="apply-phone" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Email *</label>
              <input required type="email" className={input} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Profilo Instagram</label>
              <input className={input} value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@username" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Profilo TikTok</label>
              <input className={input} value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} placeholder="@username" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Tipo di candidatura *</label>
              <select className={input} value={form.application_type} onChange={(e) => set("application_type", e.target.value)} data-testid="apply-type">
                {APP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Nome dell'attività (se presente)</label>
              <input className={input} value={form.business_name} onChange={(e) => set("business_name", e.target.value)} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Partita IVA</label>
              <select className={input} value={form.has_vat} onChange={(e) => set("has_vat", e.target.value)}>
                <option value="no">No</option>
                <option value="si">Sì</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Esperienza nel settore</label>
              <select className={input} value={form.has_experience} onChange={(e) => set("has_experience", e.target.value)}>
                <option value="no">No</option>
                <option value="si">Sì</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-500">Contatti / pubblico raggiungibile</label>
              <input className={input} value={form.reach} onChange={(e) => set("reach", e.target.value)} placeholder="Es. 5000 follower" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Breve presentazione</label>
              <textarea rows={4} className={input} value={form.presentation} onChange={(e) => set("presentation", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-zinc-500">Come hai conosciuto Vinko</label>
              <input className={input} value={form.referral} onChange={(e) => set("referral", e.target.value)} />
            </div>

            <label className="md:col-span-2 flex items-start gap-3 text-sm text-zinc-300">
              <input type="checkbox" checked={form.privacy_consent} onChange={(e) => set("privacy_consent", e.target.checked)} className="mt-1 h-4 w-4 accent-yellow-400" data-testid="apply-privacy" required />
              <span>
                Ho letto e accetto l'informativa <a href="/privacy" className="text-yellow-400 underline">Privacy</a> e acconsento al trattamento dei dati per la valutazione della candidatura.
              </span>
            </label>

            <div className="md:col-span-2">
              <button
                type="submit"
                disabled={sending}
                data-testid="apply-submit"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-60 text-black font-bold text-sm px-6 py-3 transition-transform duration-200 hover:scale-[1.02]"
              >
                {sending ? "Invio in corso..." : "INVIA LA CANDIDATURA"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
